// src/pages/RoomMaster.jsx - Complete Room Management Page
import React, { useState, useMemo } from 'react';
import { Container, Alert, Spinner, Pagination, Collapse, Button } from 'react-bootstrap';
import { FaDoorOpen, FaBuilding, FaHashtag, FaUsers, FaSync, FaDownload, FaChevronUp, FaPlus } from 'react-icons/fa';
import axios from '../api/axios';
import Cookies from 'js-cookie';
import { useCrudOperations } from '../hooks/useCrudOperations';
import { useTableOperations } from '../hooks/useTableOperations';
import GenericForm from '../components/GenericForm';
import GenericTable from '../components/GenericTable';
import SearchAndFilters from '../components/SearchAndFilters';
import GenericViewModal from '../components/GenericViewModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { exportToCSV } from '../utils/csvExport';
import { useColleges } from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const RoomMaster = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Fetch colleges dropdown data
  const { colleges, loading: collegesLoading } = useColleges();

  // Configuration for the generic CRUD
  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/master/getRoomMaster',
      save: '/master/saveRoomMaster',
      update: '/master/updateRoomMaster',
      delete: '/master/deleteRoomMaster'
    },
    validateForm: (form) => {
      const errors = {};
      if (!form.roomName.trim()) errors.roomName = 'Room name is required';
      if (!form.roomNumber.trim()) errors.roomNumber = 'Room number is required';
      if (!form.colleges) errors.colleges = 'College is required';
      if (!form.roomCapacity.trim()) errors.roomCapacity = 'Room capacity is required';
      if (isNaN(form.roomCapacity) || parseInt(form.roomCapacity) <= 0) {
        errors.roomCapacity = 'Room capacity must be a positive number';
      }
      return errors;
    },
    initialFormState: {
      roomName: '',
      roomNumber: '',
      colleges: '',
      roomCapacity: '',
      exIsActive: 1,
      user: 1,
    },
    entityName: 'Room',
    responseDataKey: 'rooms',
    // Custom toggle status transformer to include all required fields
    toggleStatusTransform: (item) => ({
      id: item.id,
      roomName: item.roomName,
      roomNumber: item.roomNumber,
      colleges: item.exCollegeId,
      roomCapacity: item.roomCapacity,
      exIsActive: item.exIsActive ? 0 : 1,
      user: 1
    })
  };

  const crud = useCrudOperations(config);
  const table = useTableOperations(crud.items, 'roomName');

  // Helper function to create dropdown options
  const createDropdownOptions = (array, loading, placeholder, labelKey = 'exName', valueKey = 'id') => {
    if (loading) {
      return [{ value: '', label: 'Loading...' }];
    }
    
    if (!array || array.length === 0) {
      return [{ value: '', label: `No ${placeholder} available` }];
    }
    
    return array.map(item => ({
      value: item[valueKey] || item.id,
      label: item[labelKey] || item.exName || item.name || `${placeholder} ${item.id}`
    }));
  };

  // Dynamic form fields
  const formFields = useMemo(() => [
    {
      name: 'roomName',
      label: 'Room Name',
      icon: FaDoorOpen,
      required: true,
      type: 'text',
      placeholder: 'Enter room name (e.g., Examination Hall)'
    },
    {
      name: 'roomNumber',
      label: 'Room Number',
      icon: FaHashtag,
      required: true,
      type: 'text',
      placeholder: 'Enter room number (e.g., 101)'
    },
    {
      name: 'colleges',
      label: 'College',
      icon: FaBuilding,
      required: true,
      as: 'select',
      disabled: collegesLoading,
      options: createDropdownOptions(colleges, collegesLoading, 'College', 'exName')
    },
    {
      name: 'roomCapacity',
      label: 'Room Capacity',
      icon: FaUsers,
      required: true,
      type: 'number',
      placeholder: 'Enter capacity (e.g., 150)'
    }
  ], [colleges, collegesLoading]);

  // Transform payload for API
  const transformPayload = (form, editId, userId) => {
    return {
      roomName: form.roomName,
      roomNumber: form.roomNumber,
      colleges: parseInt(form.colleges),
      roomCapacity: form.roomCapacity,
      exIsActive: parseInt(form.exIsActive),
      user: userId,
      ...(editId && { id: editId })
    };
  };

  // Transform item data for form editing
  const transformToForm = (item) => {
    return {
      roomName: item.roomName || '',
      roomNumber: item.roomNumber || '',
      colleges: item.exCollegeId?.toString() || '',
      roomCapacity: item.roomCapacity?.toString() || '',
      exIsActive: item.exIsActive ? 1 : 0,
      user: 1,
    };
  };

  const handleSubmit = async (e) => {
    const success = await crud.handleSubmit(e, transformPayload);
    if (success) setIsFormVisible(false);
  };

  const handleEdit = (item) => {
    crud.handleEdit(item, transformToForm);
    setIsFormVisible(true);
  };

  const handleCancelForm = () => {
    crud.resetForm();
    setIsFormVisible(false);
  };

  // Custom toggle status handler with all required fields
  const handleToggleStatus = async (item) => {
    try {
      const payload = {
        id: item.id,
        roomName: item.roomName,
        roomNumber: item.roomNumber,
        colleges: item.exCollegeId,
        roomCapacity: item.roomCapacity.toString(),
        exIsActive: item.exIsActive ? 0 : 1,
        user: 1
      };

      const token = Cookies.get('token');
      await axios.post(
        `${BASE_URL}/master/updateRoomMaster`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Refresh the data after successful toggle
      crud.fetchItems();
      crud.setSuccess('Room status updated successfully');
    } catch (error) {
      console.error('Error toggling status:', error);
      crud.setError(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleExport = () => {
    const csvHeaders = [
      { key: 'roomName', label: 'Room Name' },
      { key: 'roomNumber', label: 'Room Number' },
      { key: 'exCollege', label: 'College' },
      { key: 'roomCapacity', label: 'Capacity' },
      { key: 'status', label: 'Status' }
    ];

    const dataToExport = table.filteredAndSortedItems.map(item => ({
      ...item,
      exCollege: item.exCollege || 'N/A',
      status: item.exIsActive ? 'Active' : 'Inactive'
    }));

    exportToCSV(dataToExport, csvHeaders, 'rooms-export.csv');
  };

  // Get view fields for modal
  const getViewFields = (item) => {
    if (!item) return [];
    
    return [
      { label: 'Room Name', value: item.roomName || '-', icon: FaDoorOpen },
      { label: 'Room Number', value: item.roomNumber || '-', icon: FaHashtag },
      { label: 'College', value: item.exCollege || '-', icon: FaBuilding },
      { label: 'Room Capacity', value: item.roomCapacity || '-', icon: FaUsers }
    ];
  };

  // Show loading if colleges data is still loading
  if (collegesLoading) {
    return (
      <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading configuration...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
      {crud.success && (
        <Alert
          variant="success"
          className="position-fixed top-0 end-0 m-3 shadow-lg"
          style={{ zIndex: 1056 }}
          dismissible
          onClose={() => crud.setSuccess('')}
        >
          {crud.success}
        </Alert>
      )}
      {crud.error && (
        <Alert
          variant="danger"
          className="position-fixed top-0 end-0 m-3 shadow-lg"
          style={{ zIndex: 1056 }}
          dismissible
          onClose={() => crud.setError('')}
        >
          {crud.error}
        </Alert>
      )}

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bolder page-title">Room Management</h2>
        <div className="d-flex gap-2">
          <Button variant="outline-secondary" onClick={crud.fetchItems} disabled={crud.loading}>
            <FaSync className={crud.loading ? 'fa-spin' : ''} /> Refresh
          </Button>
          <Button variant="outline-success" onClick={handleExport}>
            <FaDownload /> Export CSV
          </Button>
          <Button
            variant="primary"
            className="btn-gradient"
            onClick={() => setIsFormVisible(!isFormVisible)}
          >
            {isFormVisible ? (
              <><FaChevronUp className="me-1" /> Hide Form</>
            ) : (
              <><FaPlus className="me-1" /> Add Room</>
            )}
          </Button>
        </div>
      </div>

      <Collapse in={isFormVisible}>
        <div>
          <GenericForm
            title="Room"
            onSubmit={handleSubmit}
            form={crud.form}
            handleChange={crud.handleChange}
            formErrors={crud.formErrors}
            submitting={crud.submitting}
            editId={crud.editId}
            onCancel={handleCancelForm}
            fields={formFields}
          />
        </div>
      </Collapse>

      <SearchAndFilters
        search={table.search}
        setSearch={table.setSearch}
        statusFilter={table.statusFilter}
        setStatusFilter={table.setStatusFilter}
        itemsPerPage={table.itemsPerPage}
        setItemsPerPage={table.setItemsPerPage}
        startIndex={table.startIndex}
        totalItems={table.filteredAndSortedItems.length}
        searchPlaceholder="Search rooms by name, number, college..."
      />

      {crud.loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" style={{ width: '3rem', height: '3rem' }} />
          <p className="mt-3 text-muted">Loading Rooms...</p>
        </div>
      ) : (
        <>
          <GenericTable
            items={table.paginatedItems}
            columns={[
              { key: 'roomName', label: 'Room Name', sortable: true },
              { key: 'roomNumber', label: 'Room Number', sortable: true },
              { 
                key: 'exCollege', 
                label: 'College', 
                sortable: true,
                render: (item) => item.exCollege || '-'
              },
              { 
                key: 'roomCapacity', 
                label: 'Capacity', 
                sortable: true,
                render: (item) => item.roomCapacity || '-'
              }
            ]}
            onSort={table.handleSort}
            onEdit={handleEdit}
            onView={(item) => { setSelectedItem(item); setShowViewModal(true); }}
            onDelete={(item) => { setSelectedItem(item); setShowDeleteModal(true); }}
            toggleStatus={handleToggleStatus}
            sortField={table.sortField}
            sortDirection={table.sortDirection}
            emptyIcon={FaDoorOpen}
            emptyTitle="No Rooms Found"
          />
          {table.totalPages > 1 && (
            <div className="d-flex justify-content-center mt-4">
              <Pagination>
                <Pagination.First
                  onClick={() => table.setCurrentPage(1)}
                  disabled={table.currentPage === 1}
                />
                <Pagination.Prev
                  onClick={() => table.setCurrentPage(c => Math.max(1, c - 1))}
                  disabled={table.currentPage === 1}
                />
                <Pagination.Item active>
                  {table.currentPage} of {table.totalPages}
                </Pagination.Item>
                <Pagination.Next
                  onClick={() => table.setCurrentPage(c => Math.min(table.totalPages, c + 1))}
                  disabled={table.currentPage === table.totalPages}
                />
                <Pagination.Last
                  onClick={() => table.setCurrentPage(table.totalPages)}
                  disabled={table.currentPage === table.totalPages}
                />
              </Pagination>
            </div>
          )}
        </>
      )}

      <GenericViewModal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        item={selectedItem}
        title="Room"
        fields={getViewFields(selectedItem)}
      />
      
      <DeleteConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        onConfirm={() => crud.handleDelete(selectedItem).then(() => setShowDeleteModal(false))}
        itemName={selectedItem?.roomName}
        submitting={crud.submitting}
      />
    </Container>
  );
};

export default RoomMaster;